import styled from 'styled-components';
import { PropsWithChildren } from 'react';

const px = (value?: number | string) =>
  typeof value === 'number' ? `${value}px` : value || '';

type ResponsiveProp<T> = T | [T, T];

interface BoxProps {
  $fill?: boolean;
  $width?: ResponsiveProp<number | string>;
  $height?: ResponsiveProp<number | string>;
  $textAlign?: 'left' | 'right' | 'center' | 'justify';
  $margin?: ResponsiveProp<number | string>;
  $marginTop?: ResponsiveProp<number | string>;
  $marginRight?: ResponsiveProp<number | string>;
  $marginBottom?: ResponsiveProp<number | string>;
  $marginLeft?: ResponsiveProp<number | string>;
  $padding?: ResponsiveProp<number | string>;
  $paddingTop?: ResponsiveProp<number | string>;
  $paddingRight?: ResponsiveProp<number | string>;
  $paddingBottom?: ResponsiveProp<number | string>;
  $paddingLeft?: ResponsiveProp<number | string>;
  $backgroundColor?: string;
  $border?: ResponsiveProp<string>;
  $borderRadius?: ResponsiveProp<number | string>;
  $display?: ResponsiveProp<
    | 'block'
    | 'inline-block'
    | 'flex'
    | 'inline-flex'
    | 'grid'
    | 'inline-grid'
    | 'none'
  >;
  $gap?: ResponsiveProp<number | string>;
  $justifyContent?: ResponsiveProp<
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
  >;
  $alignItems?: ResponsiveProp<
    'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'
  >;
  $position?: ResponsiveProp<
    'static' | 'relative' | 'absolute' | 'fixed' | 'sticky'
  >;
  $bottom?: ResponsiveProp<number | string>;
  $top?: ResponsiveProp<number | string>;
  $right?: ResponsiveProp<number | string>;
  $left?: ResponsiveProp<number | string>;
  $visibility?: ResponsiveProp<'visible' | 'hidden' | 'collapse'>;
  $alignContent?: ResponsiveProp<
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'stretch'
  >;
  $borderTop?: ResponsiveProp<string>;
  $cursor?: ResponsiveProp<'pointer' | 'default'>;
  $overflow?: ResponsiveProp<'visible' | 'hidden' | 'scroll' | 'auto'>;
  $overflowY?: ResponsiveProp<'visible' | 'hidden' | 'scroll' | 'auto'>;
  $wordWrap?: ResponsiveProp<'break-word'>;
  $zIndex?: ResponsiveProp<number>;
  $flexDirection?: ResponsiveProp<'row' | 'column'>;
  $maxWidth?: ResponsiveProp<number | string>;
  $maxHeight?: ResponsiveProp<number | string>;
  $flex?: ResponsiveProp<number>;
  $flexWrap?: ResponsiveProp<'nowrap' | 'wrap' | 'wrap-reverse'>;
  $alignSelf?: ResponsiveProp<
    'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'
  >;
  $opacity?: ResponsiveProp<number>;
  $scale?: ResponsiveProp<number>;
}

const responsive = (prop: string, value?: ResponsiveProp<string | number>) => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number')
    return `${prop}: ${px(value)};`;
  const [desktopValue, mobileValue] = value;
  return `
    ${prop}: ${px(desktopValue)};
    @media (max-width: 768px) {
      ${prop}: ${px(mobileValue)};
    }
  `;
};

const Box = styled.div<PropsWithChildren<BoxProps>>`
  ${(props) => responsive('width', props.$fill ? '100%' : props.$width)}
  ${(props) => responsive('height', props.$fill ? '100%' : props.$height)}
  ${(props) => responsive('text-align', props.$textAlign)}
  ${(props) => responsive('margin', props.$margin)}
  ${(props) => responsive('margin-top', props.$marginTop)}
  ${(props) => responsive('margin-right', props.$marginRight)}
  ${(props) => responsive('margin-bottom', props.$marginBottom)}
  ${(props) => responsive('margin-left', props.$marginLeft)}
  ${(props) => responsive('padding', props.$padding)}
  ${(props) => responsive('padding-top', props.$paddingTop)}
  ${(props) => responsive('padding-right', props.$paddingRight)}
  ${(props) => responsive('padding-bottom', props.$paddingBottom)}
  ${(props) => responsive('padding-left', props.$paddingLeft)}
  ${(props) =>
    responsive('background-color', props.$backgroundColor || 'transparent')}
  ${(props) => responsive('border', props.$border)}
  ${(props) => responsive('border-top', props.$borderTop)}
  ${(props) => responsive('border-radius', props.$borderRadius)}
  ${(props) => responsive('display', props.$display)}
  ${(props) => responsive('gap', props.$gap)}
  ${(props) => responsive('flex-direction', props.$flexDirection)}
  ${(props) => responsive('flex', props.$flex)}
  ${(props) => responsive('flex-wrap', props.$flexWrap)} // Fixed typo
  ${(props) => responsive('justify-content', props.$justifyContent)}
  ${(props) => responsive('align-items', props.$alignItems)}
  ${(props) => responsive('align-self', props.$alignSelf)}
  ${(props) => responsive('position', props.$position)}
  ${(props) => responsive('bottom', props.$bottom)}
  ${(props) => responsive('top', props.$top)}
  ${(props) => responsive('right', props.$right)}
  ${(props) => responsive('left', props.$left)}
  ${(props) => responsive('visibility', props.$visibility)}
  ${(props) => responsive('align-content', props.$alignContent)}
  ${(props) => responsive('cursor', props.$cursor)}
  ${(props) => responsive('overflow', props.$overflow)}
  ${(props) => responsive('overflow-y', props.$overflowY)}
  ${(props) => responsive('word-wrap', props.$wordWrap)}
  ${(props) => responsive('z-index', props.$zIndex)}
  ${(props) => responsive('opacity', props.$opacity)}
  ${(props) => responsive('max-height', props.$maxHeight)}
  ${(props) => responsive('max-width', props.$maxWidth)}
  ${(props) => responsive('scale', props.$scale)}
`;

export { Box };
export type { BoxProps, ResponsiveProp };
